/**
 * VqeReadinessBanner — a single banner that explains, in one place, why the
 * Visual Query Editor can't edit a statement yet and how to fix it. It replaces
 * the N identical per-control "Connect to a database…" tooltips with one
 * dismissible message.
 *
 * Six readiness states:
 *  - `ready`            → renders nothing.
 *  - `no-connection`    → dismissible; CTA opens the connection picker.
 *  - `no-database`      → NON-dismissible; connected but no database chosen,
 *                         so no schema can load; CTA opens the database picker
 *                         (full tab only — the compact pip points to the
 *                         status bar, where that tab's database is chosen).
 *  - `snapshot-loading` → NON-dismissible (it shows progress); clears itself
 *                         when the snapshot lands or the load fails.
 *  - `snapshot-failed`  → NON-dismissible; shows the engine's error (`detail`),
 *                         CTA reloads the schema.
 *  - `columns-failed`   → NON-dismissible; the tables loaded but their columns did
 *                         not — engine error (`detail`), CTA reloads the schema.
 *  - `read-only`        → NON-dismissible; CTA opens "Edit as SQL".
 *
 * Two variants: `full` (a real banner with body + CTA + dismiss ×, mounted above
 * the clause cards in the workspace) and `pip` (a compact icon + 1-line copy +
 * dismiss ×, mounted at the top of the compact popover body).
 *
 * Copy comes from `vqeCopy.banner.*` so no user-facing string is hardcoded here.
 *
 * PRD: Ralph/VqeUxRefinement/PRD.md — § Readiness banner (UX-10).
 */

import { AlertTriangle, Database, PlugZap, Lock, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '../../../../components/ui/button';
import { RandomDotmSpinner } from '../../../../components/ui/RandomDotmSpinner';
import { vqeCopy } from './vqeCopy';

export type VqeReadinessState =
  | 'ready'
  | 'no-connection'
  | 'no-database'
  | 'snapshot-loading'
  | 'snapshot-failed'
  | 'columns-failed'
  | 'read-only';

export interface VqeReadinessBannerProps {
  readonly state: VqeReadinessState;
  /** Invoked by the CTA (Connect… / Choose database / Reload schema / Edit as SQL). Omitted for snapshot-loading. */
  readonly onPrimaryAction?: () => void;
  /** Extra line under the body — the engine's error message for `snapshot-failed` / `columns-failed`. */
  readonly detail?: string;
  /** Whether this banner was dismissed this session (per-tab). */
  readonly dismissed: boolean;
  readonly onDismiss: () => void;
  /** `full` (workspace) or `pip` (compact popover). Defaults to `full`. */
  readonly variant?: 'full' | 'pip';
}

type StateConfig = {
  readonly body: string;
  readonly pip: string;
  readonly cta?: string;
  readonly icon: ReactNode;
  /** Read-only is non-dismissible and announced assertively. */
  readonly dismissible: boolean;
  readonly role: 'status' | 'alert';
};

function configFor(state: Exclude<VqeReadinessState, 'ready'>): StateConfig {
  switch (state) {
    case 'no-connection':
      return {
        body: vqeCopy.banner.noConnection.body,
        pip: vqeCopy.banner.noConnection.pip,
        cta: vqeCopy.banner.noConnection.cta,
        icon: <PlugZap className="h-4 w-4 shrink-0" aria-hidden />,
        dismissible: true,
        role: 'status',
      };
    case 'snapshot-loading':
      return {
        body: vqeCopy.banner.snapshotLoading.body,
        pip: vqeCopy.banner.snapshotLoading.pip,
        // Decorative — the banner itself is the live region (role + aria-label),
        // so the spinner's own role="status" must stay out of the a11y tree.
        icon: (
          <span className="flex shrink-0 items-center" aria-hidden>
            <RandomDotmSpinner size={16} />
          </span>
        ),
        // Progress is not something to close — it goes away on its own.
        dismissible: false,
        role: 'status',
      };
    case 'no-database':
      return {
        body: vqeCopy.banner.noDatabase.body,
        pip: vqeCopy.banner.noDatabase.pip,
        cta: vqeCopy.banner.noDatabase.cta,
        icon: <Database className="h-4 w-4 shrink-0" aria-hidden />,
        dismissible: false,
        role: 'status',
      };
    case 'snapshot-failed':
      return {
        body: vqeCopy.banner.snapshotFailed.body,
        pip: vqeCopy.banner.snapshotFailed.pip,
        cta: vqeCopy.banner.snapshotFailed.cta,
        icon: <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />,
        dismissible: false,
        role: 'alert',
      };
    case 'columns-failed':
      return {
        body: vqeCopy.banner.columnsFailed.body,
        pip: vqeCopy.banner.columnsFailed.pip,
        cta: vqeCopy.banner.columnsFailed.cta,
        icon: <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />,
        dismissible: false,
        role: 'alert',
      };
    case 'read-only':
      return {
        body: vqeCopy.banner.readOnly.body,
        pip: vqeCopy.banner.readOnly.pip,
        cta: vqeCopy.banner.readOnly.cta,
        icon: <Lock className="h-4 w-4 shrink-0" aria-hidden />,
        dismissible: false,
        role: 'alert',
      };
  }
}

export function VqeReadinessBanner({
  state,
  onPrimaryAction,
  detail,
  dismissed,
  onDismiss,
  variant = 'full',
}: VqeReadinessBannerProps) {
  if (state === 'ready') return null;

  const config = configFor(state);
  // Read-only is non-dismissible — it must always render. Everything else hides
  // once the user has dismissed it this session.
  if (dismissed && config.dismissible) return null;

  const dismissButton = config.dismissible ? (
    <button
      type="button"
      onClick={onDismiss}
      aria-label={vqeCopy.banner.dismissLabel}
      data-testid="vqe-readiness-banner-dismiss"
      className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  ) : null;

  if (variant === 'pip') {
    return (
      <div
        role={config.role}
        aria-label={config.pip}
        data-testid="vqe-readiness-banner"
        data-state={state}
        data-variant="pip"
        className="flex items-center gap-2 rounded border border-border bg-muted/50 px-2 py-1 text-xs text-muted-foreground"
      >
        {config.icon}
        <span className="min-w-0 flex-1">
          <span className="block truncate">{config.pip}</span>
          {detail ? (
            <span className="mt-0.5 block break-words" data-testid="vqe-readiness-banner-detail">
              {detail}
            </span>
          ) : null}
        </span>
        {config.cta && onPrimaryAction ? (
          <button
            type="button"
            onClick={onPrimaryAction}
            data-testid="vqe-readiness-banner-cta"
            className="shrink-0 font-medium text-foreground underline-offset-2 hover:underline"
          >
            {config.cta}
          </button>
        ) : null}
        {dismissButton}
      </div>
    );
  }

  return (
    <div
      role={config.role}
      aria-label={config.body}
      data-testid="vqe-readiness-banner"
      data-state={state}
      data-variant="full"
      className="flex items-start gap-3 border-b border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground"
    >
      {/* Top-aligned with the headline (not centred on headline + detail). */}
      <span className="mt-0.5 flex shrink-0">{config.icon}</span>
      <span className="min-w-0 flex-1">
        <span className="font-medium text-foreground">{config.body}</span>
        {detail ? (
          <span className="mt-0.5 block break-words text-xs" data-testid="vqe-readiness-banner-detail">
            {detail}
          </span>
        ) : null}
      </span>
      {config.cta && onPrimaryAction ? (
        <Button
          variant="outline"
          size="sm"
          className="self-center"
          onClick={onPrimaryAction}
          data-testid="vqe-readiness-banner-cta"
        >
          {config.cta}
        </Button>
      ) : null}
      {dismissButton}
    </div>
  );
}
